import { ErrorHandler, Injectable } from "@angular/core"
import { captureException } from "@sentry/angular-ivy"
import { addBreadcrumb, setTag } from "@sentry/browser"
import { environment } from "@env/environment"
import { Router } from "@angular/router"
import { HttpErrorResponse } from "@angular/common/http"

/**
 * Global ErrorHandler → Sentry.
 * Must NEVER pass plain objects to captureException (Sentry groups them
 * as "Object captured as exception with keys: …" — MO-FE-7C).
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor(private router: Router) {}

  handleError(error: any): void {
    const original = error
    // Zone/Angular sometimes wraps as { rejection } or promise-like
    const unwrapped = this.unwrap(error)
    const status = this.extractStatus(unwrapped, original)

    // Expected auth expiry / logout path — breadcrumb only (MO-FE-7C on /leads after 401)
    if (status === 401 || this.isUnauthorizedNoise(unwrapped, original)) {
      this.breadcrumb("Session/request unauthorized (not reported)", "info", {
        status: 401,
        url: this.safeRouterUrl(),
      })
      if (!environment.production) {
        console.warn("SentryErrorHandler suppressed 401:", unwrapped || original)
      }
      return
    }

    const normalized = this.toError(unwrapped)
    const errorMsg = normalized.message || "Unknown error"

    if (environment.production) {
      const orgId = localStorage.getItem("account_id") || "not-found"
      const userId = localStorage.getItem("User") || "not-found"

      this.breadcrumb(errorMsg, "error", {
        url: this.safeRouterUrl(),
        orgId,
        userId,
        status: status ?? null,
      })

      setTag("organization_id", orgId)
      setTag("user_id", userId)
      if (status != null) {
        setTag("http_status", String(status))
      }

      try {
        captureException(normalized)
      } catch (e) {
        // last resort — never throw from ErrorHandler
        console.error("Sentry capture failed", e, normalized)
      }
    } else {
      console.error("Captured by SentryErrorHandler:", normalized, original)
    }
  }

  private safeRouterUrl(): string {
    try {
      return this.router?.url || "(no-router)"
    } catch {
      return "(router-error)"
    }
  }

  private breadcrumb(message: string, level: "error" | "info" | "warning", data: Record<string, unknown>) {
    try {
      addBreadcrumb({ category: "error", message, level, data })
    } catch {
      /* ignore */
    }
  }

  private unwrap(error: any): any {
    if (error == null) return error
    // Promise rejection wrapper
    if (error.rejection != null) return this.unwrap(error.rejection)
    // Zone / ng being helpful
    if (error.ngOriginalError != null) return this.unwrap(error.ngOriginalError)
    // Our RequestLibrary ApiError shape: { error: { message }, message, statusCode }
    if (typeof error === "object" && error.error != null && !(error instanceof Error)) {
      // Prefer inner if it carries more signal, else keep outer
      const inner = error.error
      if (inner instanceof Error) return inner
      if (inner instanceof HttpErrorResponse) return inner
      if (typeof inner === "object" && (inner.message || inner.status || inner.statusCode)) {
        return error // keep ApiError-like so toError can read message/statusCode
      }
    }
    return error
  }

  private extractStatus(error: any, original?: any): number | null {
    const candidates = [error, original, error?.error, original?.error]
    for (const c of candidates) {
      if (c == null) continue
      if (typeof c.status === "number") return c.status
      if (typeof c.statusCode === "number") return c.statusCode
    }
    return null
  }

  private isUnauthorizedNoise(error: any, original?: any): boolean {
    const msg = String(
      error?.message ||
        error?.error?.message ||
        original?.message ||
        original?.error?.message ||
        ""
    ).toLowerCase()
    return (
      msg.includes("unauthorized") ||
      msg.includes("token is missing") ||
      msg.includes("session expired") ||
      msg.includes("invalid token")
    )
  }

  /** Always return a real Error instance for Sentry grouping. */
  private toError(error: any): Error {
    if (error instanceof Error) {
      return error
    }
    if (error instanceof HttpErrorResponse) {
      const m =
        (typeof error.error === "object" && error.error && (error.error.message || error.error.error)) ||
        error.message ||
        `HTTP ${error.status}`
      const e = new Error(String(m))
      ;(e as any).status = error.status
      ;(e as any).url = error.url
      return e
    }
    if (typeof error === "string") {
      return new Error(error)
    }
    if (error && typeof error === "object") {
      const nested = error.error
      const message =
        error.message ||
        (nested && typeof nested === "object" && (nested.message || nested.error)) ||
        (typeof nested === "string" ? nested : null) ||
        null
      if (message) {
        const e = new Error(String(message))
        if (error.statusCode != null) (e as any).status = error.statusCode
        if (error.status != null) (e as any).status = error.status
        return e
      }
      try {
        return new Error(JSON.stringify(error).substring(0, 500))
      } catch {
        return new Error("Unknown non-Error object thrown")
      }
    }
    return new Error(String(error ?? "Unknown error"))
  }
}
